import { Component, inject, ViewChild } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { CommonSharedService } from 'src/app/shared/services/common-shared.service';
import { LotSettings, State } from '../../../../../express-tRPC/src/shared/types';
import { TrpcService } from 'src/app/services/trpc/trpc.service';
import { SUREPARKgroup } from '../../../../../express-tRPC/src/api/surepark/types';
import { Router } from '@angular/router';
import { RPCLocationData } from '../../../../../express-tRPC/src/api/lots/types';
import { environment } from 'src/environments/environment';
import { NewSureparkGroupFormComponent } from 'src/app/components/new-surepark-group-form/new-surepark-group-form.component';
import { dropdownOption } from 'src/app/types/common';

@Component({
  selector: 'app-tenant-create',
  templateUrl: './tenant-create.component.html',
  styleUrl: './tenant-create.component.scss'
})
export class TenantCreateComponent {

  tenantDataLoading: boolean = false;
  // Stores the location ID the user is currently signed into
  locationId: string = '';

  states: State[] = [];
  sureparkGroupList: SUREPARKgroup[] = [];
  invoiceBatchOptions: State[] = [{ name: 'Print', code: 'print' }, { name: 'Email', code: 'email' }];
  locationSettings!: LotSettings;

  private fb = inject(FormBuilder);
  zipCodeRegex = /^(?:\d{5}|\d{5}-\d{4})$/;

  tenantForm = this.fb.group({
    suite: [{ value: '', disabled: false }, [Validators.required]],
    account: [{ value: 'Next', disabled: true }, []],
    tax_code: [""],
    name: ["", [Validators.required]],
    new_company: [false, []],
    wps_company: [""],
    // billto: [""],
    email: ["", [this.commonSharedService.multiEmailValidator]],
    address1: ["", [Validators.required]],
    address2: ["", []],
    city: ["", [Validators.required]],
    state: [""],
    zip: ["", [Validators.required, Validators.pattern(this.zipCodeRegex)]],
    invoice_delivery: ["print"]
  });

  tenantCreating: boolean = false;
  formSubmitted: boolean = false;

  isLPRActive: boolean = false;
  isCheckingLPRStatus: boolean = true;
  siteUrl = environment.SITE_URL;

  lotIdOptions: dropdownOption[] = [];
  timezoneGroupOptions: dropdownOption[] = [];

  showNewGroupDialog: boolean = false;
  newSureparkGroupData: any = undefined;

  @ViewChild(NewSureparkGroupFormComponent) newSureparkGroupFormComponent!: NewSureparkGroupFormComponent;

  constructor(
    private messageService: MessageService,
    private commonSharedService: CommonSharedService,
    private trpcService: TrpcService,
    private router: Router
  ) { }

  async ngOnInit() {
    this.tenantDataLoading = true;

    this.trpcService.trpc.lots.getSessionLID.query().then((location: string) => {
      this.locationId = location;
    });

    // Check LPR status before loading tenant list
    await this.checkLPRStatus();

    if (this.isLPRActive) {
      await this.getLocationData();
    }

    this.tenantDataLoading = false;
  }

  async checkLPRStatus() {
    try {
      this.isCheckingLPRStatus = true;
      const result = await this.trpcService.trpc.monthly_invoice.checkLPRStatus.query();
      this.isLPRActive = result.isLPRActive;
    } catch (error: any) {
      console.error('Error checking LPR status:', error);
      // Default to not active if there's an error
      this.isLPRActive = false;
    } finally {
      this.isCheckingLPRStatus = false;
    }
  }

  async loadStatesList() {
    let statesList: State[] | void = await this.trpcService.trpc.lots.getStates.query().catch((error: any) => {
      this.messageService.add({
        severity: "error",
        detail: error.message,
      });
    });

    this.states = statesList || [];
  }

  async getSettings() {

    let locationSettingsAny: any = await this.trpcService.trpc.settings.get.query().catch((error: any) => {
      this.messageService.add({
        severity: "error",
        detail: error.message,
      });
    });

    this.locationSettings = locationSettingsAny as LotSettings;

    this.lotIdOptions = locationSettingsAny?.lots || [];
    this.timezoneGroupOptions = locationSettingsAny?.timezonegroups || [];

    if (this.locationSettings.wpsActive) {
      await this.loadSureparkGroups();
    }

  }

  async getLocationData() {

    await this.loadStatesList();
    await this.getSettings();

    const locationData: any = await this.trpcService.trpc.lots.listLocations.query({ loc_id: this.locationId });
    if (locationData) {
      this.loadLocationDataInForm(locationData)
    }
  }

  loadLocationDataInForm(locationData: RPCLocationData) {
    const [address1, address2] = [locationData.address, ""];

    this.tenantForm.setValue({
      account: "Next",
      suite: "",
      tax_code: "",
      name: '',
      new_company: false,
      wps_company: "",
      // billto: tenantObject.billtocontact,
      email: "",
      address1: address1 || '',
      address2: address2 || '',
      city: locationData.city,
      state: locationData.state,
      zip: locationData.zip,
      invoice_delivery: 'print'
    });
    this.tenantForm.updateValueAndValidity();

  }

  /**
   * Retrieve the list of SUREPARK group objects for selection in the dropdown list.
   */
  async loadSureparkGroups() {
    let sureparkGroupList: SUREPARKgroup[] | void = await this.trpcService.trpc.surepark.getSureparkGroups.query().catch((error: any) => {
      this.messageService.add({
        severity: "error",
        detail: error.message,
      });
    });

    this.sureparkGroupList = [...(sureparkGroupList || [])];
  }

  displayNewGroupDialog() {
    this.showNewGroupDialog = true;
    setTimeout(() => {
      this.newSureparkGroupFormComponent.resetForm();
      if(this.tenantForm.value['new_company'] && this.newSureparkGroupData) {
        this.newSureparkGroupFormComponent.setFormValues(this.newSureparkGroupData);
      } else {
        //Tenant Name should be Default Group Name
        this.newSureparkGroupData = {
          name: this.tenantForm.controls['name'].value || '',
          cardFunction: '',
          antiPassback: '',
          stations: [],
          schedules: []
        }

        this.newSureparkGroupFormComponent.setFormValues(this.newSureparkGroupData);
      }
    }, 0);
  }

  closeNewGroupDialog() {
    this.showNewGroupDialog = false;
  }

  clearNewGroupDetails() {
    
    this.tenantForm.get('new_company')?.setValue(false);
    this.tenantForm.get('new_company')?.updateValueAndValidity();
    
    this.newSureparkGroupFormComponent.resetForm();

    const newGroupIndex = this.sureparkGroupList.findIndex((group) => group.id == '');
    if(newGroupIndex !== -1) {
      //remove new group from existing surepark group list
      this.sureparkGroupList.splice(newGroupIndex, 1);

      //Clear existing selected group
      this.tenantForm.controls.wps_company.reset();
      this.tenantForm.controls.wps_company.updateValueAndValidity();
    }

    this.closeNewGroupDialog();
  }

  saveNewGroupDetails() {
    const isValid = this.newSureparkGroupFormComponent.validateForm();
    if(!isValid) {
      this.messageService.add({
        severity: 'error',
        detail: 'Please fill required fields.'
      });
      return;
    }

    this.tenantForm.get('new_company')?.setValue(true);
    this.tenantForm.get('new_company')?.updateValueAndValidity();
    
    this.newSureparkGroupData = this.newSureparkGroupFormComponent.getValues();

    this.tenantForm.controls.wps_company.reset();
    this.tenantForm.controls.wps_company.updateValueAndValidity();
    
    const newGroup: SUREPARKgroup = {
      "id": '', //SET any string because we disn't saved group yet
      "name": this.newSureparkGroupData['name'],
      "antiPassback": this.newSureparkGroupData['antiPassback'],
      "cardFunction": this.newSureparkGroupData['cardFunction'],
      "vaultAccess": false,
      "kioskAccess": false,
      "defaultGroup": false,
      "groupTimeZoneGroups": this.newSureparkGroupData['schedules'],
      "groupStations": this.newSureparkGroupData['stations']
    };

    const newGroupIndex = this.sureparkGroupList.findIndex((group) => group.id == newGroup.id);
    
    if(newGroupIndex === -1) {
      //push new group into existing surepark group list
      this.sureparkGroupList = [...this.sureparkGroupList, newGroup];
    } else {
      this.sureparkGroupList[newGroupIndex] = newGroup;
    }
    
    this.tenantForm.controls.wps_company.setValue(newGroup.id);
    this.tenantForm.controls.wps_company.updateValueAndValidity();
    
    this.showNewGroupDialog = false;

  }

  async saveTenantData(): Promise<void> {
    this.messageService.clear();

    this.formSubmitted = true;
    if (this.tenantForm.invalid) {
      this.messageService.add({
        severity: 'error',
        detail: 'Please fill required fields.'
      });
      return;
    }

    this.formSubmitted = false;
    this.tenantCreating = true;

    const saveTenantRequest: any = this.tenantForm.getRawValue();

    if(saveTenantRequest['new_company']) {
      const newSureparkGroupDetails = structuredClone(this.newSureparkGroupData);
      saveTenantRequest['newSureparkGroupDetails'] = structuredClone(this.newSureparkGroupData);
      saveTenantRequest['newSureparkGroupDetails']['stations'] = [];
      if(newSureparkGroupDetails['stations']) {
        const selectedStation = this.lotIdOptions.find((lot: any) => newSureparkGroupDetails['stations']?.includes(lot.id));
        if(selectedStation) {
          saveTenantRequest['newSureparkGroupDetails']['stations'].push(selectedStation);
        }
      }
    }

    this.trpcService.trpc.tenant_list.saveTenantDetails.mutate(saveTenantRequest).then((response: any) => {
      this.messageService.add({
        severity: "success",
        detail: "Tenant has been saved.",
      });
      this.tenantCreating = false;

      this.router.navigate(['/tenant/list']);
    })
      .catch((error: any) => {
        // Display a more user-friendly error message for SUREPARK group conflicts
        const errorMessage = error.message;

        this.messageService.add({
          severity: "error",
          detail: errorMessage,
        });
        this.tenantCreating = false;

      });

  }
}
